import { useCallback, useRef } from "react";

type NavigationDirection = "Right" | "Left" | "Down" | "Up";

interface PositionedField {
  element: HTMLElement;
  cx: number;
  cy: number;
  row: number;
}

const ROW_GROUPING_THRESHOLD_PX = 18;

export function useFieldNavigation() {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleContentKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.defaultPrevented
      ) {
        return;
      }

      const next = getNavigationTarget(
        contentRef.current,
        event.target,
        event.key,
        event.shiftKey,
      );
      if (!next) {
        return;
      }

      event.preventDefault();
      next.focus();
    },
    [],
  );

  return {
    contentRef,
    handleContentKeyDown,
  };
}

export function getNavigationTarget(
  root: HTMLDivElement | null,
  source: EventTarget | null,
  key: string,
  shiftKey: boolean,
): HTMLElement | null {
  const direction = getNavigationDirection(key, shiftKey);
  if (!root || !direction) {
    return null;
  }

  if (!(source instanceof HTMLElement) || !isNavigableField(source)) {
    return null;
  }

  if (shouldKeepNativeBehavior(source, direction)) {
    return null;
  }

  return findNextFocusableField(root, source, direction);
}

function getNavigationDirection(
  key: string,
  shiftKey: boolean,
): NavigationDirection | null {
  if (key === "Tab") {
    return shiftKey ? "Left" : "Right";
  }
  if (key === "Enter") {
    return shiftKey ? "Up" : "Down";
  }
  return null;
}

function isNavigableField(element: HTMLElement): boolean {
  const tagName = element.tagName;
  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
    return true;
  }

  if (tagName === "BUTTON") {
    return !element.closest("nav");
  }

  return false;
}

function shouldKeepNativeBehavior(
  element: HTMLElement,
  direction: NavigationDirection,
): boolean {
  if (element instanceof HTMLTextAreaElement) {
    if (direction === "Up" || direction === "Down") {
      return true;
    }
  }

  if (element instanceof HTMLInputElement) {
    const type = (element.type || "text").toLowerCase();
    const textLike = new Set([
      "text",
      "search",
      "url",
      "tel",
      "password",
      "email",
      "number",
    ]);
    if (!textLike.has(type)) {
      return false;
    }
    if (direction === "Left" || direction === "Right") {
      return hasInnerHorizontalCaretMovement(element, direction);
    }
  }

  return false;
}

function hasInnerHorizontalCaretMovement(
  element: HTMLInputElement | HTMLTextAreaElement,
  direction: "Left" | "Right",
): boolean {
  const start = element.selectionStart;
  const end = element.selectionEnd;

  if (start === null || end === null) {
    return false;
  }

  if (start !== end) {
    return true;
  }

  if (direction === "Left") {
    return start > 0;
  }

  return end < element.value.length;
}

function findNextFocusableField(
  root: HTMLDivElement,
  source: HTMLElement,
  direction: NavigationDirection,
): HTMLElement | null {
  const fields = buildFieldLayout(root);
  if (fields.length === 0) {
    return null;
  }

  const sourceField = fields.find(field => field.element === source);
  if (!sourceField) {
    return null;
  }

  if (direction === "Left" || direction === "Right") {
    return findHorizontalNeighbor(fields, sourceField, direction);
  }

  return findVerticalNeighbor(fields, sourceField, direction);
}

function buildFieldLayout(root: HTMLDivElement): PositionedField[] {
  const focusable = getFocusableFields(root);
  const positioned = focusable
    .map(element => {
      const rect = element.getBoundingClientRect();
      return {
        element,
        cx: rect.left + rect.width / 2,
        cy: rect.top + rect.height / 2,
        row: -1,
      };
    })
    .sort((a, b) => {
      if (Math.abs(a.cy - b.cy) > ROW_GROUPING_THRESHOLD_PX) {
        return a.cy - b.cy;
      }
      return a.cx - b.cx;
    });

  let currentRow = -1;
  let rowCenterY = 0;
  let rowCount = 0;

  for (const field of positioned) {
    if (
      currentRow === -1 ||
      Math.abs(field.cy - rowCenterY) > ROW_GROUPING_THRESHOLD_PX
    ) {
      currentRow += 1;
      rowCenterY = field.cy;
      rowCount = 1;
      field.row = currentRow;
      continue;
    }

    rowCenterY = (rowCenterY * rowCount + field.cy) / (rowCount + 1);
    rowCount += 1;
    field.row = currentRow;
  }

  return positioned;
}

function findHorizontalNeighbor(
  fields: PositionedField[],
  source: PositionedField,
  direction: "Left" | "Right",
): HTMLElement | null {
  const rowFields = fields
    .filter(field => field.row === source.row)
    .sort((a, b) => a.cx - b.cx);

  const sourceIndex = rowFields.findIndex(
    field => field.element === source.element,
  );
  if (sourceIndex === -1) {
    return null;
  }

  const delta = direction === "Left" ? -1 : 1;
  const nextInRow = rowFields[sourceIndex + delta];
  if (nextInRow) {
    return nextInRow.element;
  }

  return null;
}

function findVerticalNeighbor(
  fields: PositionedField[],
  source: PositionedField,
  direction: "Up" | "Down",
): HTMLElement | null {
  const rowIndexes = Array.from(new Set(fields.map(field => field.row))).sort(
    (a, b) => a - b,
  );
  const currentRowPosition = rowIndexes.indexOf(source.row);
  if (currentRowPosition === -1) {
    return null;
  }

  const targetRowIndex =
    direction === "Up"
      ? rowIndexes[currentRowPosition - 1]
      : rowIndexes[currentRowPosition + 1];

  if (targetRowIndex === undefined) {
    return null;
  }

  const targetRowFields = fields.filter(field => field.row === targetRowIndex);
  if (targetRowFields.length === 0) {
    return null;
  }

  targetRowFields.sort((a, b) => {
    return Math.abs(a.cx - source.cx) - Math.abs(b.cx - source.cx);
  });

  return targetRowFields[0].element;
}

function getFocusableFields(root: HTMLDivElement): HTMLElement[] {
  const nodes = root.querySelectorAll(
    "input:not([disabled]):not([type='hidden']), textarea:not([disabled]), select:not([disabled]), button:not([disabled])",
  );

  return Array.from(nodes).filter(node => {
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }) as HTMLElement[];
}
